import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import SearchDropdown from "../applicationUi/components/SearchDropdown";
import { fetchAppointmentById, updateAppointmentClinician } from "../store/thunks/appointmentsThunk";
import { updateClientClinician } from "../store/thunks/clientsThunk";
import { toast } from "react-toastify";

export default function ModifyClinicianModal({
  isOpen,
  onClose,
  appointment,
  client,
  clinicianType, // "treating" or "supervising"
  context = "appointment", // "appointment" or "client"
  onSuccess,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [updateAllFuture, setUpdateAllFuture] = useState(false);

  // Get current clinician based on type and context
  const currentClinician = context === "appointment" 
    ? (clinicianType === "treating" ? appointment?.clinician : appointment?.supervisingClinician)
    : (clinicianType === "treating" ? client?.treatingClinician?.current : client?.supervisingClinician?.current);

  // Get future appointments count for client context
  const futureAppointmentsCount = client?.appointments?.filter(apt => {
    const isFuture = new Date(apt.start) >= new Date();
    // Check if any status in the array matches cancelled/no-show statuses
    const hasCancelledStatus = apt.status?.some(status => 
      ["Cancelled", "Cancelled - No Fee", "Cancelled - Charge Fee", "Cancelled - Clinician", "Cancelled - Client", "No Show", "No Show - No Fee", "No Show - Charge Fee"].includes(status)
    );
    const isNotCancelled = !hasCancelledStatus;
    return isFuture && isNotCancelled;
  }).length || 0;

  // Get future appointments for appointment context (excluding current appointment)
  const futureAppointmentsForClient = client?.appointments?.filter(apt => {
    const isFuture = new Date(apt.start) >= new Date();
    const hasCancelledStatus = apt.status?.some(status => 
      ["Cancelled", "Cancelled - No Fee", "Cancelled - Charge Fee", "Cancelled - Clinician", "Cancelled - Client", "No Show", "No Show - No Fee", "No Show - Charge Fee"].includes(status)
    );
    const isNotCancelled = !hasCancelledStatus;
    const isNotCurrentAppointment = appointment ? apt._id !== appointment._id : true;
    return isFuture && isNotCancelled && isNotCurrentAppointment;
  }).length || 0;

  // Debug logging
  console.log("=== FUTURE APPOINTMENTS DEBUG ===");
  console.log("Client appointments:", client?.appointments?.length);
  console.log("Client appointments data:", client?.appointments);
  console.log("Future appointments count (client context):", futureAppointmentsCount);
  console.log("Future appointments count (appointment context):", futureAppointmentsForClient);
  console.log("Current date:", new Date());
  if (client?.appointments) {
    client.appointments.forEach((apt, index) => {
      console.log(`Appointment ${index}:`, {
        id: apt._id,
        start: apt.start,
        status: apt.status,
        isFuture: new Date(apt.start) >= new Date(),
        hasCancelledStatus: apt.status?.some(status => 
          ["Cancelled", "Cancelled - No Fee", "Cancelled - Charge Fee", "Cancelled - Clinician", "Cancelled - Client", "No Show", "No Show - No Fee", "No Show - Charge Fee"].includes(status)
        )
      });
    });
  }

  useEffect(() => {
    if (isOpen) {
      setSelectedClinician(null);
      setUpdateAllFuture(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!selectedClinician) return;

    setIsLoading(true);
    try {
      if (context === "appointment") {
        // Always use updateAppointmentClinician for appointment context
        const result = await dispatch(updateAppointmentClinician({
          id: appointment._id,
          clinicianType: clinicianType,
          clinicianId: selectedClinician._id,
          updateAllFuture: updateAllFuture
        })).unwrap();

        const actionText = updateAllFuture 
          ? `for this appointment and ${result.appointmentsUpdated} future appointments` 
          : "for this appointment only";
        
        // Show conflict warning if conflicts were detected
        if (result.conflictsDetected > 0) {
          toast.warning(`${clinicianType === "treating" ? "Treating" : "Supervising"} clinician updated ${actionText}. ${result.conflictsDetected} conflict(s) detected - check admin dashboard for resolution.`);
        } else {
          toast.success(`${clinicianType === "treating" ? "Treating" : "Supervising"} clinician updated ${actionText}`);
        }

        // Refresh the appointment data
        await dispatch(fetchAppointmentById(appointment._id));
      } else {
        // Update client clinician
        const result = await dispatch(updateClientClinician({
          clientId: client._id,
          clinicianType: clinicianType,
          clinicianId: selectedClinician._id,
          updateAppointments: updateAllFuture
        })).unwrap();

        const actionText = updateAllFuture 
          ? `for ${client.firstName} and ${result.appointmentsUpdated} future appointments` 
          : `for ${client.firstName}`;
        
        // Show conflict warning if conflicts were detected
        if (updateAllFuture && result.conflictsDetected > 0) {
          toast.warning(`${clinicianType === "treating" ? "Treating" : "Supervising"} clinician updated ${actionText}. ${result.conflictsDetected} conflict(s) detected - check admin dashboard for resolution.`);
        } else {
          toast.success(`${clinicianType === "treating" ? "Treating" : "Supervising"} clinician updated ${actionText}`);
        }
      }
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error(`Failed to update ${clinicianType} clinician:`, error);
      toast.error(`Failed to update ${clinicianType} clinician. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedClinician(null);
    setUpdateAllFuture(false);
    onClose();
  };

  const clinicianTypeDisplay = clinicianType === "treating" ? "Treating" : "Supervising";

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-600/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="relative transform overflow-hidden rounded-sm bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-950 focus:ring-offset-1"
                    onClick={handleClose}>
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Modify {clinicianTypeDisplay} Clinician
                    </Dialog.Title>

                    <div className="mt-6 space-y-6">
                      {/* Warning Icon and Message */}
                      <div className="flex items-start space-x-3">
                        <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-700 mb-2">
                            You are about to change the {clinicianTypeDisplay.toLowerCase()} clinician to{" "}
                            <span className="font-semibold">
                              {selectedClinician ? `${selectedClinician.firstName} ${selectedClinician.lastName}` : "a new clinician"}
                            </span>
                            .
                          </p>
                          {context === "client" && futureAppointmentsCount > 0 && (
                            <p className="text-sm text-gray-700">
                              {client?.firstName} has future appointments that may be affected by this change.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Current Clinician Display */}
                      <div className="rounded-sm border border-gray-200 bg-gray-50 p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Currently assigned {clinicianTypeDisplay.toLowerCase()} clinician:
                        </h4>
                        <p className="text-sm text-gray-700">
                          {currentClinician 
                            ? `${currentClinician.firstName} ${currentClinician.lastName}`
                            : "None assigned"
                          }
                        </p>
                      </div>

                      {/* Search for New Clinician */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Search for a new {clinicianTypeDisplay.toLowerCase()} clinician:
                        </label>
                        <SearchDropdown
                          prefix=":clinician:"
                          displayFields={["firstName", "lastName", "qualifications"]}
                          onSelection={setSelectedClinician}
                          placeholder="Search for a clinician"
                        />
                      </div>

                      {/* Selected Clinician Confirmation */}
                      {selectedClinician && (
                        <div className="rounded-sm border border-blue-200 bg-blue-50 p-4">
                          <h4 className="text-sm font-medium text-blue-900 mb-2">
                            Change the {clinicianTypeDisplay.toLowerCase()} clinician to:
                          </h4>
                          <p className="text-sm text-blue-800">
                            {selectedClinician.firstName} {selectedClinician.lastName}
                            {selectedClinician.qualifications && (
                              <span className="text-blue-600 ml-2">
                                ({selectedClinician.qualifications})
                              </span>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Radio Options - Context Aware */}
                      {selectedClinician && (
                        <div className="space-y-3">
                          <p className="text-sm font-medium text-gray-900">
                            Would you like to:
                          </p>
                          <div className="space-y-2">
                            {context === "client" ? (
                              // Client context options
                              <>
                                <label className="flex items-center space-x-3 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="updateOption"
                                    value="all"
                                    checked={updateAllFuture}
                                    onChange={() => setUpdateAllFuture(true)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                  />
                                  <span className="text-sm text-gray-700">
                                    Change {clinicianTypeDisplay.toLowerCase()} clinician for all {client?.firstName}'s future appointments
                                  </span>
                                </label>
                                <label className="flex items-center space-x-3 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="updateOption"
                                    value="current"
                                    checked={!updateAllFuture}
                                    onChange={() => setUpdateAllFuture(false)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                  />
                                  <span className="text-sm text-gray-700">
                                    Do not change {clinicianTypeDisplay.toLowerCase()} clinician for {client?.firstName}'s future appointments
                                  </span>
                                </label>
                              </>
                            ) : (
                              // Appointment context options
                              <>
                                <label className="flex items-center space-x-3 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="updateOption"
                                    value="current"
                                    checked={!updateAllFuture}
                                    onChange={() => setUpdateAllFuture(false)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                  />
                                  <span className="text-sm text-gray-700">
                                    Modify {clinicianTypeDisplay.toLowerCase()} clinician for only this appointment
                                  </span>
                                </label>
                                <label className="flex items-center space-x-3 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="updateOption"
                                    value="all"
                                    checked={updateAllFuture}
                                    onChange={() => setUpdateAllFuture(true)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                  />
                                  <span className="text-sm text-gray-700">
                                    Modify {clinicianTypeDisplay.toLowerCase()} clinician for this and all future appointments
                                  </span>
                                </label>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 sm:mt-6 sm:flex sm:flex-row-reverse sm:gap-3">
                  <button
                    type="button"
                    disabled={!selectedClinician || isLoading}
                    onClick={handleSave}
                    className={`inline-flex w-full justify-center rounded-sm px-3 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto ${
                      !selectedClinician || isLoading
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    }`}>
                    {isLoading ? "Saving..." : "Update Clinician"}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="mt-3 inline-flex w-full justify-center rounded-sm bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
} 